/* RevShift Media — rs-reviews.js: Ducci's own review display built from Judge.me's data (no Judge.me widget).
   Used by sections/rs-reviews.liquid (<rs-reviews>). 2026-10-18.

   1. Reads Judge.me's server-rendered review HTML (product metafield judgeme.widget) from an inert <template>,
      parses it with DOMParser into plain data, and draws Ducci's review cards and rating bars from that data.
      Only text and https image URLs are taken from the markup; nothing of Judge.me's HTML is inserted.
   2. "Show more": Judge.me's public widget API (page 2 onward), parsed the same way. Needs the public token.
   3. "Write a review" (off by default): posts to Judge.me's public review endpoint.
   Tolerant by design: markup it doesn't recognise yields no reviews, and the list simply stays empty. */
(() => {
  if (customElements.get('rs-reviews')) return;

  const API = 'https://judge.me/api/v1';
  const txt = (el) => (el ? el.textContent.replace(/\s+/g, ' ').trim() : '');
  const isHttp = (u) => typeof u === 'string' && /^(https:)?\/\//.test(u);

  /* Judge.me review widget HTML -> { reviews: [...], bars: [...], perPage } */
  function parseWidget(html) {
    const out = { reviews: [], bars: [], perPage: 0 };
    if (!html || typeof html !== 'string') return out;
    let doc;
    try { doc = new DOMParser().parseFromString(html, 'text/html'); } catch (e) { return out; }
    doc.querySelectorAll('.jdgm-rev').forEach((el) => {
      const scoreEl = el.querySelector('[data-score]');
      const rating = scoreEl ? parseFloat(scoreEl.getAttribute('data-score')) : NaN;
      const bodyEl = el.querySelector('.jdgm-rev__body');
      const body = bodyEl ? [...bodyEl.querySelectorAll('p')].map(txt).filter(Boolean) : [];
      if (bodyEl && !body.length && txt(bodyEl)) body.push(txt(bodyEl));
      const timeEl = el.querySelector('.jdgm-rev__timestamp');
      const pics = [];
      el.querySelectorAll('.jdgm-rev__pics img, .jdgm-rev__pic-img').forEach((img) => {
        const src = img.getAttribute('data-src') || img.getAttribute('src');
        if (isHttp(src) && pics.length < 6) pics.push(src.startsWith('//') ? 'https:' + src : src);
      });
      const review = {
        rating,
        author: txt(el.querySelector('.jdgm-rev__author')),
        date: timeEl ? (timeEl.getAttribute('data-content') || txt(timeEl)) : '',
        title: txt(el.querySelector('.jdgm-rev__title')),
        body,
        pics,
        verified: !!el.querySelector('.jdgm-rev__buyer-badge'),
      };
      if (rating >= 1 && rating <= 5 && (review.title || review.body.length)) out.reviews.push(review);
    });
    doc.querySelectorAll('.jdgm-histogram__row[data-rating]').forEach((row) => {
      const rating = parseInt(row.getAttribute('data-rating'), 10);
      const count = parseInt(row.getAttribute('data-frequency'), 10) || 0;
      if (rating >= 1 && rating <= 5) out.bars.push({ rating, count });
    });
    const widg = doc.querySelector('[data-per-page]');
    out.perPage = widg ? parseInt(widg.getAttribute('data-per-page'), 10) || 0 : 0;
    return out;
  }

  class RsReviews extends HTMLElement {
    connectedCallback() {
      this.list = this.querySelector('[data-rs-list]');
      this.page = 1;
      this.shown = 0;
      this.count = parseInt(this.dataset.count, 10) || 0;
      const tpl = this.querySelector('template[data-rs-source]');
      const data = parseWidget(tpl ? tpl.innerHTML : '');
      this.perPage = data.perPage || data.reviews.length || 5;
      this.renderBars(data.bars);
      this.renderReviews(data.reviews);
      this.setupMore();
      this.setupForm();
    }

    el(tag, cls, text) {
      const e = document.createElement(tag);
      if (cls) e.className = cls;
      if (text) e.textContent = text;
      return e;
    }

    stars(rating) {
      const s = this.el('span', 'rs-rstars rs-rstars--sm');
      s.style.setProperty('--rs-rstars', Math.max(0, Math.min(100, (rating / 5) * 100)) + '%');
      s.setAttribute('role', 'img');
      s.setAttribute('aria-label', (this.dataset.stars || '[r] / 5').replace('[r]', rating));
      return s;
    }

    date(value) {
      if (!value) return '';
      const d = new Date(String(value).replace(' UTC', 'Z').replace(' ', 'T'));
      if (isNaN(d)) return '';
      try {
        return new Intl.DateTimeFormat(this.dataset.locale || document.documentElement.lang || 'en', { year: 'numeric', month: 'long' }).format(d);
      } catch (e) { return ''; }
    }

    renderBars(bars) {
      const box = this.querySelector('[data-rs-bars]');
      if (!box || bars.length < 2) return;
      const total = bars.reduce((n, b) => n + b.count, 0);
      if (!total) return;
      bars.sort((a, b) => b.rating - a.rating).forEach((b) => {
        const li = this.el('li', 'rs-reviews__bar');
        li.append(this.el('span', 'rs-reviews__bar-k', String(b.rating)));
        const track = this.el('span', 'rs-reviews__bar-track');
        const fill = this.el('i');
        fill.style.width = Math.round((b.count / total) * 100) + '%';
        track.append(fill);
        li.append(track, this.el('span', 'rs-reviews__bar-n', String(b.count)));
        box.append(li);
      });
      box.hidden = false;
    }

    renderReviews(reviews) {
      reviews.forEach((r) => {
        const li = this.el('li', 'rs-review');
        const head = this.el('div', 'rs-review__head');
        head.append(this.stars(r.rating));
        if (r.verified) head.append(this.el('span', 'rs-review__badge', this.dataset.verified || ''));
        li.append(head);
        if (r.title) li.append(this.el('h3', 'rs-review__title', r.title));
        r.body.forEach((p) => li.append(this.el('p', 'rs-review__body', p)));
        if (r.pics.length) {
          const pics = this.el('div', 'rs-review__pics');
          r.pics.forEach((src, i) => {
            const a = this.el('a', 'rs-review__pic');
            a.href = src; a.target = '_blank'; a.rel = 'noopener';
            const img = this.el('img');
            img.src = src; img.loading = 'lazy'; img.alt = (this.dataset.photo || 'Photo') + ' ' + (i + 1);
            img.width = 88; img.height = 88;
            a.append(img); pics.append(a);
          });
          li.append(pics);
        }
        const foot = this.el('p', 'rs-review__foot');
        if (r.author) foot.append(this.el('b', '', r.author));
        const when = this.date(r.date);
        if (when) foot.append(this.el('span', '', when));
        if (foot.childNodes.length) li.append(foot);
        this.list.append(li);
      });
      this.shown += reviews.length;
    }

    setupMore() {
      const btn = this.querySelector('[data-rs-more]');
      const token = this.dataset.token;
      if (!btn || !token || this.shown === 0 || this.shown >= this.count) return;
      btn.hidden = false;
      btn.addEventListener('click', async () => {
        btn.disabled = true;
        const page = this.page + 1;
        const q = new URLSearchParams({ api_token: token, shop_domain: this.dataset.shop, handle: this.dataset.handle, page: String(page) });
        try {
          const res = await fetch(API + '/widgets/product_review?' + q.toString());
          const json = res.ok ? await res.json() : null;
          const data = parseWidget(json && json.widget);
          if (data.reviews.length) {
            this.page = page;
            this.renderReviews(data.reviews);
          }
          btn.hidden = !data.reviews.length || this.shown >= this.count;
        } catch (e) {
          btn.hidden = true;
        }
        btn.disabled = false;
      });
    }

    setupForm() {
      const open = this.querySelector('[data-rs-write]');
      const form = this.querySelector('[data-rs-form]');
      if (!open || !form) return;
      const status = form.querySelector('[data-rs-status]');
      open.addEventListener('click', () => {
        form.hidden = !form.hidden;
        open.setAttribute('aria-expanded', String(!form.hidden));
        if (!form.hidden) form.querySelector('input')?.focus();
      });
      form.addEventListener('submit', async (e) => {
        e.preventDefault();
        if (!form.checkValidity()) { status.textContent = status.dataset.missing || ''; return; }
        const f = new FormData(form);
        const body = new URLSearchParams({
          shop_domain: this.dataset.shop, platform: 'shopify', id: this.dataset.productId,
          name: f.get('name'), email: f.get('email'), rating: f.get('rating'), title: f.get('title'), body: f.get('body'),
        });
        const submit = form.querySelector('[type="submit"]');
        submit.disabled = true;
        try {
          const res = await fetch(API + '/reviews', { method: 'POST', body });
          if (!res.ok) throw new Error(String(res.status));
          form.reset();
          [...form.elements].forEach((x) => { x.disabled = true; });
          status.textContent = status.dataset.ok || '';
        } catch (err) {
          status.textContent = status.dataset.err || '';
          submit.disabled = false;
        }
      });
    }
  }

  customElements.define('rs-reviews', RsReviews);
  window.RsReviewsParse = parseWidget; // used by the QA harness (theme_build/pdp/qa)
})();
